/**
 * Class representing the HousingReport Period Selects.
 */
export default class HousingReportPeriodSelects {
    DataPageInstance;

    buttons;

    /**
     * Create a HousingReportPeriodSelects instance.
     * @param {Object} DataPageInstance - The instance of the Data Page.
     */
    constructor(DataPageInstance) {
        this.DataPageInstance = DataPageInstance;

        this.buttons = document.querySelectorAll('.js-period-button');

        //
        this.initializePeriodButtons();
    }

    /**
     * Initialize period buttons and add event listeners.
     */
    initializePeriodButtons() {
        let periodButtons = this.getPeriodButtons();

        if (periodButtons.length === 0) {
            throw Error(
                'There is no element on the page with the attribute "data-table-button="{type}".',
            );
        }

        periodButtons.forEach((button) => {
            button.addEventListener(
                'click',
                this.periodButtonEventHandler.bind(this),
            );
        });
    }

    /**
     * Handle the click event of a period button.
     * @param {Event} event - The click event.
     */
    periodButtonEventHandler(event) {
        event.preventDefault();
        let periodButtons = this.getPeriodButtons();
        const button = event.currentTarget;

        //
        periodButtons.forEach((el) => {
            this.setButtonUnclickable(el);
        });

        //
        this.setButtonClickable(button);

        //
        this.eventPeriodChange();
    }

    /**
     * Get the period buttons.
     * @returns {NodeList} - The list of period buttons.
     */
    getPeriodButtons() {
        return this.buttons;
    }

    /**
     * Get the active period.
     * @returns {string} - The active period ('annual' or 'monthly').
     */
    getActivePeriod() {
        let periodButtons = this.getPeriodButtons();

        for (const key in periodButtons) {
            const button = periodButtons[key];

            if (button.classList.contains('active')) {
                return button.getAttribute('data-table-button');
            }
        }

        return 'overall';
    }

    setButtonUnclickable(button) {
        if (!(button instanceof HTMLElement)) {
            return;
        }
        button.classList.remove('active');
        button.classList.remove('a-btn--style-closed-type-01');
        button.classList.add('a-btn--style-open-type-01');
        button.classList.remove('pointer-events-none');
    }

    setButtonClickable(button) {
        if (!(button instanceof HTMLElement)) {
            return;
        }
        button.classList.add('active');
        button.classList.add('a-btn--style-closed-type-01');
        button.classList.remove('a-btn--style-open-type-01');
        button.classList.add('pointer-events-none');
    }

    /**
     * Set the active period and update the appearance of period buttons accordingly.
     *
     * @param {string} period - The period to set as active.
     */
    setPeriod(period) {
        // Get all period buttons
        let periodButtons = this.getPeriodButtons();

        // Iterate through each period button
        periodButtons.forEach((el) => {
            // Get the data attribute value representing the period associated with the button
            const btnPeriod = el.dataset.tableButton;

            // Check if the button's period matches the provided period
            if (btnPeriod === period) {
                // Set the button as active and apply specific CSS classes
                this.setButtonClickable(el);
            } else {
                // Remove active state and reset CSS classes for other buttons
                this.setButtonUnclickable(el);
            }
        });
    }

    /**
     * Disable (forbid) a specific period in the user interface.
     *
     * @param {string} period - The period to be disabled.
     */
    forbidPeriod(period) {
        let periodButtons = this.getPeriodButtons();

        periodButtons.forEach((button) => {
            const btnPeriod = button.dataset.tableButton;

            if (btnPeriod === period) {
                // Disable the button by adding specific CSS classes
                button.classList.add('js-period-forbid');
            }
        });
    }

    /**
     * Enable all period buttons in the user interface.
     */
    allowAllPeriods() {
        let periodButtons = this.getPeriodButtons();

        periodButtons.forEach((button) => {
            // Remove the CSS classes that disable the button
            button.classList.remove('js-period-forbid');
        });
    }

    /**
     * Disable the period buttons.
     */
    disableSelects() {
        let periodButtons = this.getPeriodButtons();
        periodButtons.forEach((button) => {
            if (button.classList.contains('active')) {
                return;
            }
            button.classList.add(
                'pointer-events-none',
                'cursor-not-allowed',
                'opacity-50',
            );
        });
    }

    /**
     * Enable the period buttons.
     */
    enableSelects() {
        let periodButtons = this.getPeriodButtons();
        periodButtons.forEach((button) => {
            if (button.classList.contains('active')) {
                return;
            }
            button.classList.remove(
                'cursor-not-allowed',
                'pointer-events-none',
                'opacity-50',
            );
        });
    }

    /**
     * Trigger the 'gridPeriod.Change' event.
     */
    eventPeriodChange() {
        const event = new CustomEvent('gridPeriod.Change');

        document.dispatchEvent(event);
    }
}
